import { useQuery } from '@tanstack/react-query';
import { apiV2 } from 'api/ApiV2';
import Loader from 'components/common/Loader/Loader';
import ProductCard from 'components/common/ProductCard';
import { checkReplaceProductImageHost } from 'helpers/utils';
import IMerchCategory from 'models/restful/IMerchCategory';
import IMerchProduct from 'models/restful/IMerchProduct';
import IMerchProductOrder from 'models/restful/IMerchProductOrder';
import IMerchProductOrderItem from 'models/restful/IMerchProductOrderItem';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import './Products.scss';
import ProductListFilter from './ProductsListFilter/ProductsListFilter';
import { TMerchProductType } from 'models/e-commerce/TMerchProductType';

interface IProductsProps {
  order: IMerchProductOrder;
  showRequired: boolean;
  setOrder(order: IMerchProductOrder): void;
}

const productToEcomerceProduct = (
  product: IMerchProduct
): TMerchProductType => {
  const image = checkReplaceProductImageHost(product?._props?.previewImageUrl);

  return {
    id: product.ID,
    origin: product,
    image: image,
    name: product.Title,
    salePrice: product.FixedPrice
    // rating: 5, // Default rating since it's not available in IMerchProduct
    // price: product.FixedPrice,
    // colors: product.Colors?.length || 0,
    // extra: product.Description,
    // extraClass: 'text-1000 fs--1 mb-0 fw-bold',
    // extra2: product.Categories?.[0]?.Title,
    // extra2Class: 'text-700 fs--1 mb-2'
  };
};

const Products: React.FunctionComponent<IProductsProps> = ({
  order,
  ...props
}: IProductsProps) => {
  const [selectedCats, setSelectedCats] = useState<IMerchCategory[]>([]);
  const [productsToShow, setProductsToShow] = useState(
    new Set<IMerchProduct['ID']>()
  );
  const [checkedProducts, setCheckedProducts] = useState(
    new Set(order.ProductOrderItems.map(oi => oi.ProductID))
  );
  useEffect(() => {
    setCheckedProducts(
      new Set(order.ProductOrderItems.map(oi => oi.ProductID))
    );
  }, [order]);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch categories using React Query
  const { data: shopData, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['shop-data'],
    queryFn: () => apiV2.category.getShopData(),
    staleTime: 5 * 60 * 1000 // Consider data fresh for 5 minutes
  });

  const productsList = useMemo(() => {
    if (!shopData) return [];

    const { products } = shopData;
    const filteredProducts = products
      .filter(product => {
        const isMatchingSearch =
          searchTerm.trim().length === 0 ||
          product.Title.toLowerCase().includes(searchTerm.toLowerCase());

        if (!isMatchingSearch) return false;

        return productsToShow.size === 0 || productsToShow.has(product.ID);
      })
      .map(p => productToEcomerceProduct(p));

    return filteredProducts;
  }, [selectedCats, productsToShow, searchTerm, shopData]);

  const handleProductToggle = async (product: TMerchProductType) => {
    const isSelected = !checkedProducts.has(product.id);
    try {
      if (isSelected) {
        checkedProducts.add(product.id);
      } else {
        checkedProducts.delete(product.id);
      }

      setCheckedProducts(new Set(Array.from(checkedProducts)));

      const payload = {
        ProductID: product.id,
        OrderID: order.ID
      } as IMerchProductOrderItem;

      const response = isSelected
        ? await apiV2.order.createOrderItem({
            orderItem: payload
          })
        : await apiV2.order.deleteOrderItem({
            orderItem: payload
          });

      props.setOrder(response.item);
    } catch (error) {
      console.error('Error toggling selected product:', error);
    }
  };

  return (
    <div className="products-cont">
      {props.showRequired && checkedProducts.size === 0 && (
        <div className="alert alert-danger" role="alert">
          Select at least one product
        </div>
      )}
      {isLoadingCategories && <Loader />}
      {!isLoadingCategories &&
        shopData?.categories &&
        shopData.categories.length > 0 && (
          <div className="row">
            <div className="col-12">
              <ProductListFilter
                categories={shopData.categories}
                setSearchTerm={setSearchTerm}
                searchTerm={searchTerm}
                setSelectedCats={newSelectedCats => {
                  setSelectedCats(newSelectedCats);
                  setProductsToShow(
                    new Set(
                      (
                        newSelectedCats as unknown as Omit<
                          IMerchCategory,
                          'Products'
                        > &
                          { Products: number[] }[]
                      ).flatMap(cat => cat.Products)
                    )
                  );
                }}
              />
            </div>
            <Col>
              <Row className="gx-4 gy-4 mb-8">
                {productsList.map((product, idx) => (
                  <Col
                    key={`${product.id}-${idx}`}
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    xxl={2}
                  >
                    <div className="product-card-container h-100">
                      <ProductCard
                        variant="checkbox"
                        product={product}
                        checked={checkedProducts.has(product.id)}
                        onCheckedChange={() => handleProductToggle(product)}
                      />
                    </div>
                  </Col>
                ))}
                {productsList.length === 0 && <h3>Select a category</h3>}
              </Row>
            </Col>
          </div>
        )}
    </div>
  );
};

export default Products;
